import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Save, RotateCcw } from 'lucide-react';

/**
 * Reusable popup editor for inline content (text or button label+link).
 * fields: [{ name, label, type: 'text'|'textarea', value, placeholder }]
 */
export default function EditContentPopup({ title = 'Edit', fields, onSave, onReset, onClose }) {
  const [vals, setVals] = useState(() => Object.fromEntries(fields.map(f => [f.name, f.value ?? ''])));
  const [saving, setSaving] = useState(false);

  const setVal = (name, v) => setVals(prev => ({ ...prev, [name]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(vals);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this to the original built-in text?')) return;
    setSaving(true);
    try {
      await onReset();
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
      data-testid="edit-content-popup"
      onClick={onClose}
      style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", letterSpacing: 'normal', textShadow: 'none' }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1B4B5A]">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  value={vals[f.name]}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  rows={4}
                  placeholder={f.placeholder}
                  data-testid={`popup-field-${f.name}`}
                  className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:border-[#B5813B]"
                  autoFocus
                />
              ) : (
                <input
                  type="text"
                  value={vals[f.name]}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`popup-field-${f.name}`}
                  className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:border-[#B5813B]"
                />
              )}
              {f.hint && <p className="text-xs text-gray-500 mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 mt-5">
          {onReset ? (
            <button
              onClick={handleReset}
              disabled={saving}
              data-testid="popup-reset"
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-red-600 font-semibold"
            >
              <RotateCcw className="w-4 h-4" /> Reset to original
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="popup-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1B4B5A] text-white font-semibold hover:bg-[#143a45] disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
